import React, { useState, useEffect } from "react";
import { 
  Phone, Calendar, Filter, Download, RefreshCw, 
  User, Building2, Clock, CheckCircle, XCircle, ChevronDown
} from "lucide-react";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDateTime, cn } from "../lib/utils";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "all", label: "All Calls", color: "bg-gray-100 text-gray-800" },
  { value: "lead", label: "Leads", color: "bg-purple-100 text-purple-800" },
  { value: "due", label: "Due", color: "bg-yellow-100 text-yellow-800" },
  { value: "overdue", label: "Overdue", color: "bg-orange-100 text-orange-800" },
  { value: "delayed", label: "Delayed", color: "bg-blue-100 text-blue-800" },
  { value: "no_show", label: "No Show", color: "bg-red-100 text-red-800" }
];

const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "custom", label: "Custom Range" }
];

const getOutcomeBadge = (outcome) => {
  switch (outcome) {
    case "answered": return <Badge className="bg-green-100 text-green-800">Answered</Badge>;
    case "no_answer": return <Badge className="bg-yellow-100 text-yellow-800">No Answer</Badge>;
    case "busy": return <Badge className="bg-orange-100 text-orange-800">Busy</Badge>;
    case "wrong_number": return <Badge className="bg-red-100 text-red-800">Wrong Number</Badge>;
    case "voicemail": return <Badge className="bg-blue-100 text-blue-800">Voicemail</Badge>;
    default: return <Badge variant="secondary">{outcome || "-"}</Badge>;
  }
};

const getCategoryBadge = (category) => {
  const cat = CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  return <Badge className={cat.color}>{cat.label}</Badge>;
};

export const CallsDonePage = () => {
  const { user, isAdmin } = useAuth();
  const [callLogs, setCallLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [category, setCategory] = useState("all");
  const [datePreset, setDatePreset] = useState("today");
  const [dateFrom, setDateFrom] = useState(format(new Date(), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    applyDatePreset(datePreset);
  }, [datePreset]);

  useEffect(() => {
    fetchCallLogs();
    fetchSummary();
  }, [category, dateFrom, dateTo, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const applyDatePreset = (preset) => {
    const today = new Date();
    let from, to;
    
    switch (preset) {
      case "today":
        from = to = format(today, "yyyy-MM-dd");
        break;
      case "yesterday":
        const yesterday = subDays(today, 1);
        from = to = format(yesterday, "yyyy-MM-dd");
        break;
      case "this_week":
        from = format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd");
        to = format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd");
        break;
      case "this_month":
        from = format(startOfMonth(today), "yyyy-MM-dd");
        to = format(endOfMonth(today), "yyyy-MM-dd");
        break;
      case "custom":
        setShowDatePicker(true);
        return;
      default:
        from = to = format(today, "yyyy-MM-dd");
    }
    
    setDateFrom(from);
    setDateTo(to);
    setShowDatePicker(false);
  };

  const fetchCallLogs = async () => {
    setLoading(true);
    try {
      let url = `/call-logs/calls-done?date_from=${dateFrom}&date_to=${dateTo}`;
      
      if (!isAdmin()) {
        url += `&cre_user_id=${user.id}`;
      }
      
      if (category !== "all") {
        url += `&category=${category}`;
      }
      
      if (selectedBranch && selectedBranch !== "all") {
        url += `&branch_id=${selectedBranch}`;
      }
      
      const data = await apiRequest(url);
      setCallLogs(data);
    } catch (error) {
      toast.error("Failed to fetch call logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      let url = `/call-logs/summary?date_from=${dateFrom}&date_to=${dateTo}`;
      
      if (!isAdmin()) {
        url += `&cre_user_id=${user.id}`;
      }
      
      if (selectedBranch && selectedBranch !== "all") {
        url += `&branch_ids=${selectedBranch}`;
      }
      
      const data = await apiRequest(url);
      setSummary(data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  };

  const handleRefresh = () => {
    fetchCallLogs();
    fetchSummary();
  };

  return (
    <div className="space-y-6" data-testid="calls-done-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Calls Done</h1>
          <p className="text-muted-foreground">Historical call activity by category</p>
        </div>
        <Button variant="outline" onClick={handleRefresh} data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card className="bento-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => setCategory("all")}>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">Total Calls</p>
              <p className="text-2xl font-bold">{summary.total}</p>
            </CardContent>
          </Card>
          <Card className={`bento-card cursor-pointer hover:shadow-md transition-shadow ${category === 'lead' ? 'ring-2 ring-purple-500' : ''}`} onClick={() => setCategory("lead")}>
            <CardContent className="p-3">
              <p className="text-xs text-purple-700">Leads</p>
              <p className="text-2xl font-bold text-purple-700">{summary.lead}</p>
            </CardContent>
          </Card>
          <Card className={`bento-card cursor-pointer hover:shadow-md transition-shadow ${category === 'due' ? 'ring-2 ring-yellow-500' : ''}`} onClick={() => setCategory("due")}>
            <CardContent className="p-3">
              <p className="text-xs text-yellow-700">Due</p>
              <p className="text-2xl font-bold text-yellow-700">{summary.due}</p>
            </CardContent>
          </Card>
          <Card className={`bento-card cursor-pointer hover:shadow-md transition-shadow ${category === 'overdue' ? 'ring-2 ring-orange-500' : ''}`} onClick={() => setCategory("overdue")}>
            <CardContent className="p-3">
              <p className="text-xs text-orange-700">Overdue</p>
              <p className="text-2xl font-bold text-orange-700">{summary.overdue}</p>
            </CardContent>
          </Card>
          <Card className={`bento-card cursor-pointer hover:shadow-md transition-shadow ${category === 'delayed' ? 'ring-2 ring-blue-500' : ''}`} onClick={() => setCategory("delayed")}>
            <CardContent className="p-3">
              <p className="text-xs text-blue-700">Delayed</p>
              <p className="text-2xl font-bold text-blue-700">{summary.delayed}</p>
            </CardContent>
          </Card>
          <Card className={`bento-card cursor-pointer hover:shadow-md transition-shadow ${category === 'no_show' ? 'ring-2 ring-red-500' : ''}`} onClick={() => setCategory("no_show")}>
            <CardContent className="p-3">
              <p className="text-xs text-red-700">No Show</p>
              <p className="text-2xl font-bold text-red-700">{summary.no_show}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bento-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Preset */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger className="w-[140px]" data-testid="date-preset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Range */}
            {(datePreset === "custom" || showDatePicker) && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[140px]"
                  data-testid="date-from-input"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[140px]"
                  data-testid="date-to-input"
                />
              </div>
            )}

            {/* Branch Filter (Admin only) */}
            {isAdmin() && (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="w-[160px]" data-testid="branch-filter">
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-[140px]" data-testid="category-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Display */}
            <div className="ml-auto text-sm text-muted-foreground">
              {dateFrom === dateTo ? (
                <span>{format(new Date(dateFrom), "dd MMM yyyy")}</span>
              ) : (
                <span>{format(new Date(dateFrom), "dd MMM")} - {format(new Date(dateTo), "dd MMM yyyy")}</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call Logs Table */}
      <Card className="bento-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#ED1C24]" />
            Call History
            <Badge variant="secondary" className="ml-2">{callLogs.length} calls</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Date/Time</TableHead>
                  {isAdmin() && <TableHead>CRE</TableHead>}
                  <TableHead>Customer</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Remark</TableHead>
                  <TableHead>Follow-up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin() ? 9 : 8} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : callLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin() ? 9 : 8} className="text-center py-8 text-muted-foreground">
                      <Phone className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No calls found for the selected filters</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  callLogs.map((log) => (
                    <TableRow key={log.id} className="table-high-density" data-testid={`call-row-${log.id}`}>
                      <TableCell className="font-mono text-sm">
                        {formatDateTime(log.created_at)}
                      </TableCell>
                      {isAdmin() && (
                        <TableCell className="text-sm">{log.cre_name || "-"}</TableCell>
                      )}
                      <TableCell className="font-medium">{log.contact_name || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatPhone(log.contact_phone)}
                      </TableCell>
                      <TableCell className="text-sm">{log.branch_name || "-"}</TableCell>
                      <TableCell>{getCategoryBadge(log.category)}</TableCell>
                      <TableCell>{getOutcomeBadge(log.outcome)}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {log.remark || "-"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.follow_up_date ? format(new Date(log.follow_up_date), "dd MMM") : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CallsDonePage;
